import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Gift, Loader2, Search, Lock, Pencil, X, CheckCircle, RefreshCw, AlertCircle } from "lucide-react";

export default function AdminRedemptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobile, setMobile] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [edit, setEdit] = useState({ open: false, redemption: null, slot: "dealer_1", dealerId: "", saving: false, error: "" });
  const [resyncing, setResyncing] = useState({});

  const load = useCallback((mob) => {
    setLoading(true);
    const q = mob ? `?mobile=${encodeURIComponent(mob)}` : "";
    API.get(`/admin/redemptions${q}`).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(""); }, [load]);
  useEffect(() => { API.get("/admin/distributors?limit=1000").then(r => setDistributors(r.data?.distributors || (Array.isArray(r.data) ? r.data : []))).catch(() => {}); }, []);

  const openEdit = (redemption, slot) => setEdit({ open: true, redemption, slot, dealerId: "", saving: false, error: "" });
  const closeEdit = () => setEdit({ open: false, redemption: null, slot: "dealer_1", dealerId: "", saving: false, error: "" });

  const saveDealer = async () => {
    if (!edit.dealerId) { setEdit(e => ({ ...e, error: "Select a distributor" })); return; }
    setEdit(e => ({ ...e, saving: true, error: "" }));
    try {
      const d = distributors.find(x => x.db_code === edit.dealerId);
      const { data } = await API.put(`/admin/redemptions/${edit.redemption.unique_id}/dealer`, {
        dealer_id: edit.dealerId, dealer_name: d?.name, slot: edit.slot,
      });
      const updated = data.redemption;
      setItems(prev => prev.map(it => (it.unique_id === updated.unique_id ? { ...it, ...updated } : it)));
      closeEdit();
    } catch (err) {
      setEdit(e => ({ ...e, saving: false, error: err?.response?.data?.detail || "Could not update dealer" }));
    }
  };

  const dealerLabel = (d) => (d ? `${d.name || ""}${d.db_code ? ` (${d.db_code})` : ""}` : "—");

  const resync = async (uniqueId) => {
    setResyncing(prev => ({ ...prev, [uniqueId]: true }));
    try {
      const { data } = await API.post(`/admin/redemptions/${uniqueId}/resync-erp`);
      setItems(prev => prev.map(it => (it.unique_id === uniqueId ? { ...it, erp_synced: data.ok, erp_synced_count: data.synced, erp_last_error: data.error || null } : it)));
    } catch {
      /* keep prior status; badge stays "failed" */
    } finally {
      setResyncing(prev => ({ ...prev, [uniqueId]: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-redemptions">
      <div className="flex items-center gap-3">
        <Gift className="w-6 h-6 text-[#008A99]" />
        <div><h1 className="text-2xl font-bold text-gray-900">Redemptions Report</h1><p className="text-sm text-gray-500">{items.length} redemption(s)</p></div>
      </div>

      {/* Search by mobile */}
      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={mobile}
            onChange={e => setMobile(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load(mobile.trim())}
            placeholder="Search customer by mobile number"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#008A99]"
            data-testid="redemption-mobile-search"
          />
        </div>
        <button onClick={() => load(mobile.trim())} className="px-4 py-2 bg-[#008A99] text-white rounded-lg text-sm font-semibold" data-testid="redemption-search-btn">Search</button>
        {mobile && <button onClick={() => { setMobile(""); load(""); }} className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600">Clear</button>}
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> : (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b">
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Redemption ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">1st Dealer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">2nd Dealer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">OTP</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">ERP Sync</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Delivery</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
            </tr></thead>
            <tbody>
              {items.map(item => {
                const locked = !!(item.otp_validated || item.dealer_locked);
                const d1 = item.dealer_1 || (item.distributor_name ? { name: item.distributor_name, db_code: item.distributor_id } : null);
                const d2 = item.dealer_2;
                return (
                  <tr key={item.id || item.unique_id} className="border-b last:border-0 hover:bg-gray-50 align-top" data-testid={`redemption-row-${item.unique_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-[#008A99]">{item.unique_id}</td>
                    <td className="px-4 py-3"><div className="font-medium">{item.user_name}</div><div className="text-xs text-gray-400">{item.user_mobile}</div></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span data-testid={`dealer1-${item.unique_id}`}>{dealerLabel(d1)}</span>
                        {!locked && (
                          <button onClick={() => openEdit(item, "dealer_1")} className="text-gray-400 hover:text-[#008A99]" title="Change 1st dealer" data-testid={`edit-dealer1-${item.unique_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span data-testid={`dealer2-${item.unique_id}`}>{dealerLabel(d2)}</span>
                        {!locked && (
                          <button onClick={() => openEdit(item, "dealer_2")} className="text-gray-400 hover:text-[#008A99]" title="Change 2nd dealer" data-testid={`edit-dealer2-${item.unique_id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {locked ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" data-testid={`otp-validated-${item.unique_id}`}><Lock className="w-3 h-3" /> Validated</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`otp-pending-${item.unique_id}`}>Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {item.erp_synced ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" data-testid={`erp-synced-${item.unique_id}`}><CheckCircle className="w-3 h-3" /> Synced</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" title={item.erp_last_error || "Not yet synced"} data-testid={`erp-failed-${item.unique_id}`}><AlertCircle className="w-3 h-3" /> Not synced</span>
                        )}
                        <button
                          onClick={() => resync(item.unique_id)}
                          disabled={!!resyncing[item.unique_id]}
                          className="text-gray-400 hover:text-[#008A99] disabled:opacity-50"
                          title="Resync to TransAsia ERP"
                          data-testid={`resync-erp-${item.unique_id}`}
                        >
                          {resyncing[item.unique_id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.delivered || item.material_received ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" data-testid={`delivered-${item.unique_id}`}><CheckCircle className="w-3 h-3" /> Delivered</span>
                      ) : item.otp_validated ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium" data-testid={`awaiting-delivery-${item.unique_id}`}>Awaiting confirmation</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium" data-testid={`not-delivered-${item.unique_id}`}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-gray-400">No redemptions found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Dealer edit modal */}
      {edit.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="dealer-edit-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-gray-900">Change {edit.slot === "dealer_1" ? "1st" : "2nd"} Dealer</h3>
              <button onClick={closeEdit}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-1">Redemption</p>
            <p className="text-xs font-mono text-[#008A99] mb-3">{edit.redemption?.unique_id}</p>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Distributor</label>
            <select
              value={edit.dealerId}
              onChange={e => setEdit(s => ({ ...s, dealerId: e.target.value }))}
              className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#008A99]"
              data-testid="dealer-edit-select"
            >
              <option value="">Select distributor…</option>
              {distributors.map(d => <option key={d.db_code} value={d.db_code}>{d.name} ({d.db_code})</option>)}
            </select>
            {edit.error && <p className="text-xs text-red-500 mt-2" data-testid="dealer-edit-error">{edit.error}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={closeEdit} disabled={edit.saving} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={saveDealer} disabled={edit.saving} className="flex-1 py-2 bg-[#008A99] text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5" data-testid="dealer-edit-save">
                {edit.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
